/**
 * The app's button. Named `Btn` in the monolith; the export keeps that alias so
 * call sites read identically after the split.
 *
 * Styles are transcribed verbatim from the prototype — same padding, radius, font
 * and variant colours. Do not "tidy" these numbers: they are the design.
 *
 * THE ONE EXCEPTION is the disabled fade, which moved from 0.5 to the shared
 * DISABLED_OPACITY — see the note on it in styles/theme.ts for the measurements. A
 * disabled control still has to be readable: it names the action you cannot
 * currently take, and "Save" faded to illegibility reads as a rendering fault
 * rather than a state. It is still plainly disabled — the fade, the cursor and the
 * real `disabled` attribute each say so.
 *
 * This matters more since the lyric editor landed: Save sits disabled until the
 * sheet is edited, which is the first thing you see on opening the editor.
 */
import { useState, type CSSProperties, type MouseEvent, type ReactNode, type Ref } from 'react';
import { Loader2 } from 'lucide-react';
import { c, DISABLED_OPACITY } from '../../styles/theme';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

export interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  /**
   * Renders the control as an anchor while retaining the app's button styling.
   *
   * Checkout links use this so their destination is present in the DOM and remains
   * available to link inspectors and native browser actions such as open-in-new-tab.
   */
  href?: string;
  /** Uses the checkout provider's image as the entire buy control. */
  checkoutImageUrl?: string;
  /** Shows a spinner and blocks repeat clicks while an href navigation begins. */
  pendingOnNavigate?: boolean;
  variant?: ButtonVariant;
  /** Stretches the button to its container's width. */
  full?: boolean;
  /**
   * A ref onto the <button>, for a caller that has to move focus to it.
   *
   * Settings' name editor needs it: closing the editor (saved or cancelled) has to hand
   * focus back to the Edit control it came from, and focus left on a button that has just
   * been unmounted lands on <body> — where the next Tab restarts from the top of the page.
   *
   * A named prop rather than `forwardRef`, matching TextField's `inputRef`: this component
   * destructures its props deliberately (see the note on `aria-pressed`), so a ref has to be
   * declared to arrive at all.
   */
  buttonRef?: Ref<HTMLButtonElement>;
  style?: CSSProperties;
  disabled?: boolean;
  type?: 'button' | 'submit';
  'aria-label'?: string;
  /**
   * Id of the element that explains this button, e.g. why it is disabled.
   *
   * Needed because a disabled button fires no pointer events, so a Tooltip wrapped
   * around one never opens — the explanation has to be associated with the element
   * itself. The composer's Generate button uses it.
   */
  'aria-describedby'?: string;
  /** Reflects a pressed/expanded state to assistive technology. */
  'aria-expanded'?: boolean;
  /**
   * Reflects an on/off state for a button that toggles rather than navigates.
   *
   * The lyrics preview's Like button needs it: the label alone would say "Like" while the
   * track is already liked, so the state has to be on the element. Declared explicitly
   * because this component destructures its props rather than spreading them — an
   * undeclared `aria-*` here is silently dropped, which is worse than a type error.
   */
  'aria-pressed'?: boolean;
}

const VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: { background: c.accentFill, color: '#1A1300' },
  secondary: { background: c.surface3, color: c.text1, border: `1px solid ${c.border}` },
  ghost: { background: 'transparent', color: c.text2 },
};

export default function Button({
  children,
  onClick,
  href,
  checkoutImageUrl,
  pendingOnNavigate,
  variant = 'primary',
  full,
  buttonRef,
  style,
  disabled,
  type = 'button',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-expanded': ariaExpanded,
  'aria-pressed': ariaPressed,
}: ButtonProps) {
  const [navigationPending, setNavigationPending] = useState(false);
  const effectivelyDisabled = disabled || navigationPending;
  const base: CSSProperties = {
    fontSize: 13,
    padding: '9px 16px',
    borderRadius: 8,
    // A disabled or already-started action is unavailable, not a background task the
    // pointer needs to wait for. Keep the spinner/aria-busy state for navigation, but use
    // the same disabled cursor for both the native button and checkout anchor.
    cursor: effectivelyDisabled ? 'not-allowed' : 'pointer',
    fontFamily: 'Inter',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: full ? '100%' : 'auto',
    opacity: disabled ? DISABLED_OPACITY : 1,
    border: 'none',
    textDecoration: 'none',
    ...style,
  };

  if (href && checkoutImageUrl && !disabled) {
    return (
      <a
        href={href}
        role="button"
        aria-label={ariaLabel}
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <img
          key={checkoutImageUrl}
          src={checkoutImageUrl}
          alt={ariaLabel ?? (typeof children === 'string' ? children : 'Buy now')}
          style={{ maxWidth: '100%', height: 'auto', border: 0 }}
        />
      </a>
    );
  }

  if (href && !disabled) {
    return (
      <a
        href={href}
        role="button"
        aria-label={navigationPending ? `${ariaLabel ?? 'Checkout'} — loading` : ariaLabel}
        aria-busy={navigationPending || undefined}
        aria-disabled={navigationPending || undefined}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-pressed={ariaPressed}
        onClick={
          onClick
            ? (event: MouseEvent<HTMLAnchorElement>) => {
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  return;
                }
                event.preventDefault();
                if (navigationPending) return;
                if (pendingOnNavigate) {
                  setNavigationPending(true);
                  window.setTimeout(onClick, 0);
                  return;
                }
                onClick();
              }
            : pendingOnNavigate
              ? (event: MouseEvent<HTMLAnchorElement>) => {
                  if (
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  ) {
                    return;
                  }
                  event.preventDefault();
                  if (navigationPending) return;
                  setNavigationPending(true);
                  window.setTimeout(() => window.location.assign(href), 0);
                }
              : undefined
        }
        style={{ ...base, position: 'relative', ...VARIANTS[variant] }}
      >
        <span
          aria-hidden={navigationPending || undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, opacity: navigationPending ? 0 : 1 }}
        >
          {children}
        </span>
        {navigationPending ? (
          <span
            style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Loader2 size={14} className="lyx-spin" aria-hidden="true" />
          </span>
        ) : null}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef}
      type={type}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-pressed={ariaPressed}
      // `disabled` already blocks the click; passing undefined as well matches the
      // prototype and keeps the handler from firing if the attribute is ever
      // overridden via `style`/props spreading.
      onClick={disabled ? undefined : onClick}
      style={{ ...base, ...VARIANTS[variant] }}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** The monolith's name for this component. */
export { Button as Btn };
